/**
 * "Summarize with AI" — the assistants the footer links to, and the URL each
 * one accepts a prompt on. Verified 2 Sep 2026 (see the research note in the
 * PR/commit); none of these parameters is covered by a change policy, so a
 * provider changing its URL is a one-line edit here.
 *
 *  - ChatGPT   chatgpt.com/?q=  auto-sends; `hints=search` opens web-search
 *              mode, which a "summarize this URL" prompt needs. `?prompt=` is
 *              the prefill-only alternative. chat.openai.com is only a 308.
 *  - Claude    claude.ai/new?q=  prefills; the visitor presses Send. Claude
 *              strips `<` and `>` from the prompt, so the builder removes them.
 *  - Gemini    gemini.google.com/app?prompt_text=  prefills (read by Google's
 *              own client bundle; `q`/`prompt`/`text` are ignored). Needs a
 *              Google sign-in. If it stops working, swap in
 *              GOOGLE_AI_MODE_TEMPLATE and relabel "Google AI Mode".
 *  - Perplexity perplexity.ai/search/new?q=  runs immediately (declared by
 *              Perplexity's own opensearch.xml). /search?q= is a 301 to it.
 *  - Grok      grok.com/?q=  auto-sends, no X login. x.com/i/grok?text= is
 *              gated behind an X login — do not use it.
 *
 * `{prompt}` in a template is replaced with encodeURIComponent(prompt).
 */
export type AiProviderId = 'chatgpt' | 'claude' | 'gemini' | 'perplexity' | 'grok';

export interface AiProvider {
  id: AiProviderId;
  label: string;
  /** Absolute URL with a single `{prompt}` placeholder. */
  template: string;
}

export const AI_PROVIDERS: readonly AiProvider[] = [
  { id: 'chatgpt', label: 'ChatGPT', template: 'https://chatgpt.com/?q={prompt}&hints=search' },
  { id: 'claude', label: 'Claude', template: 'https://claude.ai/new?q={prompt}' },
  { id: 'gemini', label: 'Gemini', template: 'https://gemini.google.com/app?prompt_text={prompt}' },
  { id: 'perplexity', label: 'Perplexity', template: 'https://www.perplexity.ai/search/new?q={prompt}' },
  { id: 'grok', label: 'Grok', template: 'https://grok.com/?q={prompt}' },
];

export const AI_PROVIDER_IDS: readonly AiProviderId[] = AI_PROVIDERS.map((p) => p.id);

/** Google AI Mode: runs the prompt with no sign-in. Fallback for the Gemini entry (see above). */
export const GOOGLE_AI_MODE_TEMPLATE = 'https://www.google.com/search?udm=50&aep=11&q={prompt}';

/** Default prompt. `{url}` = the page's canonical URL, `{title}` = the document title. */
export const DEFAULT_SUMMARIZE_PROMPT =
  'Summarize this page and explain what OfficePortal does, in a few bullet points: {url}';

/** Admin-field cap for the template (characters). Over SUMMARIZE_PROMPT_HARD_MAX the builder falls back to the default template; the URL itself is never cut. */
export const SUMMARIZE_PROMPT_MAX = 500;
/** Longest prompt we will ever put in a URL — comfortably under every provider's practical limit (~2,000 chars encoded). */
export const SUMMARIZE_PROMPT_HARD_MAX = 1500;

export const SUMMARIZE_LABEL = 'Summarize with AI';
export const AI_INFO_LABEL = 'Hey AI, learn about us';
